import { Grid } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { KeyedMutator } from 'swr/_internal';
import { LoadingButton } from '@mui/lab';
import { usePostHog } from 'posthog-js/react';
import TitledDrawer from '../../common/ui/TitledDrawer';
import {
  AccountData,
  AccountService,
  CreateStorefrontInput,
  PartialLabelData,
  StorefrontCustomInquiryData,
  StorefrontData,
  StorefrontFAQData,
  StorefrontReviewData,
  StorefrontSection,
  StorefrontStylingData,
  UpdateStorefrontInput,
} from '../../api';
import { useSafeExecute } from '../../common/apiNotifications/safeExecute';
import { OrderPagesPanel } from './panels/OrderPagesPanel';
import { TrialOrderPagesPanel } from './panels/TrialOrderPagesPanel';
import { ReviewsPanel } from './panels/ReviewsPanel';
import { FAQsPanel } from './panels/FAQsPanel';
import { StylingPanel } from './panels/StylingPanel';
import { CustomInquiryPanel } from './panels/CustomInquiryPanel';
import { FormTextField } from '../../experienceSettings/formComponents/FormTextField';
import { PassOrderPagesPanel } from './panels/PassOrderPagesPanel';
import { getPassOrderPages } from '../../utils/AccountUtils';

export enum StorefrontPanels {
  OrderPages = 'order_pages',
  PassOrderPages = 'pass_order_pages',
  Trials = 'trial_order_pages',
  Reviews = 'reviews',
  FAQs = 'faqs',
  CustomInquiry = 'custom_inquiry',
  Styling = 'styling',
}

type StorefrontDrawerProps = {
  open: boolean;
  onClose: () => void;
  storefront?: StorefrontData;
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  existingStorefronts: StorefrontData[];
};

const DEFAULT_DISPLAY_SECTIONS: StorefrontSection[] = [
  StorefrontSection.ORDER_PAGES,
  StorefrontSection.TRIALS,
  StorefrontSection.PASS_ORDER_PAGES,
  StorefrontSection.REVIEWS,
  StorefrontSection.FAQS,
  StorefrontSection.ABOUT,
];

type FormValues = {
  name: string;
  order_page_ids: string[];
  pass_order_page_ids: string[];
  trial_order_page_ids: string[];
  reviews: StorefrontReviewData[];
  faqs: StorefrontFAQData[];
  styling?: StorefrontStylingData;
  subtitle?: PartialLabelData;
  displaySections: StorefrontSection[];
  custom_inquiry?: StorefrontCustomInquiryData;
};

export const StorefrontDrawer = ({
  open,
  onClose,
  storefront,
  account,
  updateLocalAccount,
  existingStorefronts,
}: StorefrontDrawerProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const posthog = usePostHog();
  const [expandedPanel, setExpandedPanel] = useState<StorefrontPanels | undefined>(StorefrontPanels.OrderPages);
  const [loading, setLoading] = useState(false);

  const isEditing = !!storefront;
  const title = isEditing ? t('storefronts.edit_storefront') : t('storefronts.create_storefront');

  const getDefaultValues = () => {
    if (isEditing) {
      return {
        reviews: storefront.reviews || [],
        faqs: storefront.faqs || [],
        styling: storefront.styling,
        custom_inquiry: storefront.custom_inquiry,
      };
    }
    const firstStorefront = existingStorefronts[0];
    if (firstStorefront) {
      return {
        reviews: firstStorefront.reviews?.map((r) => ({ ...r, id: undefined })) || [],
        faqs: firstStorefront.faqs?.map((f) => ({ ...f, id: undefined })) || [],
        styling: firstStorefront.styling,
        custom_inquiry: firstStorefront.custom_inquiry,
      };
    }
    return { reviews: [], faqs: [], styling: undefined, custom_inquiry: undefined };
  };

  const defaultValues = getDefaultValues();

  const hasPassOrderPages = getPassOrderPages(account).length > 0;

  const initialValues: FormValues = {
    name: storefront?.name || '',
    order_page_ids: storefront?.order_page_ids || [],
    pass_order_page_ids: storefront?.pass_order_page_ids || [],
    trial_order_page_ids: storefront?.trial_order_page_ids || [],
    reviews: defaultValues.reviews,
    faqs: defaultValues.faqs,
    styling: defaultValues.styling,
    subtitle: storefront?.subtitle || {},
    custom_inquiry: defaultValues.custom_inquiry || {},
    displaySections:
      storefront?.displaySections && storefront.displaySections.length > 0
        ? storefront?.displaySections
        : DEFAULT_DISPLAY_SECTIONS,
  };

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string().required(t('storefronts.name')),
      order_page_ids: Yup.array().when(['pass_order_page_ids', 'trial_order_page_ids'], {
        is: (passIds: string[], trialIds: string[]) =>
          (!passIds || passIds.length === 0) && (!trialIds || trialIds.length === 0),
        then: (schema) => schema.min(1, t('storefronts.at_least_one_order_page')),
      }),
      pass_order_page_ids: Yup.array().when(['order_page_ids', 'trial_order_page_ids'], {
        is: (orderIds: string[], trialIds: string[]) =>
          (!orderIds || orderIds.length === 0) && (!trialIds || trialIds.length === 0),
        then: (schema) => schema.min(1, t('storefronts.at_least_one_order_page')),
      }),
      trial_order_page_ids: Yup.array().when(['order_page_ids', 'pass_order_page_ids'], {
        is: (orderIds: string[], passIds: string[]) =>
          (!orderIds || orderIds.length === 0) && (!passIds || passIds.length === 0),
        then: (schema) => schema.min(1, t('storefronts.at_least_one_order_page')),
      }),
    },
    [
      ['order_page_ids', 'pass_order_page_ids'],
      ['order_page_ids', 'trial_order_page_ids'],
      ['pass_order_page_ids', 'trial_order_page_ids'],
    ],
  );

  const handlePanelChange = (panel: StorefrontPanels) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : undefined);
  };

  const handleSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      if (isEditing) {
        const input: UpdateStorefrontInput = {
          id: storefront.id,
          name: values.name,
          order_page_ids: values.order_page_ids,
          pass_order_page_ids: values.pass_order_page_ids,
          trial_order_page_ids: values.trial_order_page_ids,
          reviews: values.reviews,
          faqs: values.faqs,
          styling: values.styling,
          subtitle: values.subtitle,
          custom_inquiry: values.custom_inquiry,
          displaySections: values.displaySections.length > 0 ? values.displaySections : DEFAULT_DISPLAY_SECTIONS,
        };
        const updatedAccount = await safeExecute({
          apiCall: () => AccountService.updateStorefront(input),
          successMessage: t('storefronts.update_success'),
        });
        await updateLocalAccount(updatedAccount);
        posthog.capture('Minisite updated', { storefrontId: storefront.id, name: values.name });
      } else {
        const input: CreateStorefrontInput = {
          name: values.name,
          order_page_ids: values.order_page_ids,
          pass_order_page_ids: values.pass_order_page_ids,
          trial_order_page_ids: values.trial_order_page_ids,
          reviews: values.reviews,
          faqs: values.faqs,
          styling: values.styling,
          subtitle: values.subtitle,
          custom_inquiry: values.custom_inquiry,
          displaySections: values.displaySections.length > 0 ? values.displaySections : DEFAULT_DISPLAY_SECTIONS,
        };
        const updatedAccount = await safeExecute({
          apiCall: () => AccountService.createStorefront(input),
          successMessage: t('storefronts.create_success'),
        });
        await updateLocalAccount(updatedAccount);
        posthog.capture('Minisite created', { name: values.name });
      }
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <TitledDrawer title={title} open={open} onClose={onClose}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({ values, errors, touched, setFieldValue, handleChange }) => (
          <Form style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Grid
              container
              flexDirection="column"
              gap={1.5}
              flexWrap="nowrap"
              sx={{
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
                pt: { xs: '12px', md: '18px' },
                pb: 2,
                paddingInline: { xs: '12px', md: '24px' },
              }}>
              <FormTextField
                width="100%"
                label={t('storefronts.name')}
                placeholder={t('storefronts.name_placeholder')}
                fieldName="name"
                isFieldUpdate
                disabled={false}
              />

              <OrderPagesPanel
                account={account}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                expanded={expandedPanel}
                onPanelChange={handlePanelChange}
                panelName={StorefrontPanels.OrderPages}
              />

              <TrialOrderPagesPanel
                account={account}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                expanded={expandedPanel}
                onPanelChange={handlePanelChange}
                panelName={StorefrontPanels.Trials}
              />

              {hasPassOrderPages && (
                <PassOrderPagesPanel
                  account={account}
                  selectedIds={values.pass_order_page_ids}
                  onChange={(ids) => setFieldValue('pass_order_page_ids', ids)}
                  expanded={expandedPanel}
                  onPanelChange={handlePanelChange}
                  panelName={StorefrontPanels.PassOrderPages}
                  error={touched.pass_order_page_ids ? (errors.pass_order_page_ids as string) : undefined}
                />
              )}

              <ReviewsPanel
                reviews={values.reviews}
                onChange={(reviews) => setFieldValue('reviews', reviews)}
                expanded={expandedPanel}
                onPanelChange={handlePanelChange}
                panelName={StorefrontPanels.Reviews}
              />

              <FAQsPanel
                faqs={values.faqs}
                onChange={(faqs) => setFieldValue('faqs', faqs)}
                expanded={expandedPanel}
                onPanelChange={handlePanelChange}
                panelName={StorefrontPanels.FAQs}
              />

              <CustomInquiryPanel
                expanded={expandedPanel}
                onPanelChange={handlePanelChange}
                panelName={StorefrontPanels.CustomInquiry}
              />

              <StylingPanel
                expanded={expandedPanel}
                onPanelChange={handlePanelChange}
                panelName={StorefrontPanels.Styling}
              />
            </Grid>

            <Grid
              container
              justifyContent="flex-end"
              sx={{
                flexShrink: 0,
                py: 1.5,
                paddingInline: { xs: '12px', md: '24px' },
                borderTop: '1px solid',
                borderColor: 'mb.lineSoft',
              }}>
              <LoadingButton loading={loading} variant="contained" type="submit">
                {t('storefronts.save')}
              </LoadingButton>
            </Grid>
          </Form>
        )}
      </Formik>
    </TitledDrawer>
  );
};
